import React, { useState, useEffect } from "react"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlay, faSave } from "@fortawesome/free-solid-svg-icons"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import ExportDataModal from "./ExportDataModal.component"
import { Tooltip } from "antd"
import { QueryInstance } from "types/engine.types"

interface QueryConsoleActionsProps {
  /** Function to run the script written in the editor */
  runScript: () => void
  /** Function to save the project query */
  saveProjectQuery: (queryName: string) => void
  /** The query instance ID */
  queryInstance: QueryInstance | null
  /** Boolean indicating whether the script is currently running */
  isRunningScript: boolean
  /** Whether the current view has a runnable script (e.g. Builder needs row+col vars) */
  canRun: boolean
  /** The selected query */
  selectedQuery: string
  /** Function to set the selected query */
  setSelectedQuery: (query: string) => void
  /** Boolean indicating whether the content has changed */
  contentChanged: boolean
  /** The active key of the tab */
  activeKey?: string
  /** Hide the Run button when the current view doesn't run from this row
   *  (e.g. Transform has its own Apply button). */
  hideRun?: boolean
  /** Hide the Save button when the current view has its own kind-aware
   *  Save (Builder toolbar / Transform pipeline bar). */
  hideSave?: boolean
  /** When true, the Save button always opens the "Save as" modal — used
   *  when the editor is showing a structured record (pipeline/builder)
   *  so we don't accidentally overwrite it with a script-kind body. */
  forceSaveAs?: boolean
}

const isMac = (): boolean =>
  window.navigator.platform.toLowerCase().includes("mac")

/**
 * QueryConsoleActions — Run, Save, Export buttons plus the saved-state hairline.
 *
 * The previous "View Saved Queries" toggle was removed in favour of the
 * QueryConsoleViewMode tab ribbon at the container level. The pulsing-dot
 * saved indicator was replaced with a hairline bar beneath the Save button
 * (signature move #4 in the QueryConsole design language).
 */
const QueryConsoleActions: React.FC<QueryConsoleActionsProps> = ({
  runScript,
  saveProjectQuery,
  queryInstance,
  isRunningScript,
  canRun,
  selectedQuery,
  setSelectedQuery,
  contentChanged,
  activeKey,
  hideRun = false,
  hideSave = false,
  forceSaveAs = false,
}) => {
  const [isSaveQueryModalVisible, setIsSaveQueryModalVisible] =
    useState<boolean>(false)
  const isUnsaved: boolean = contentChanged && selectedQuery !== ""
  const saveShortcut: string = isMac() ? "⌘ + S" : "Ctrl + S"

  const confirmSaveQuery = (queryName: string): void => {
    saveProjectQuery(queryName)
    setSelectedQuery(queryName)
    setIsSaveQueryModalVisible(false)
  }

  const handleSaveClick = (): void => {
    if (selectedQuery && !forceSaveAs) {
      confirmSaveQuery(selectedQuery)
    } else {
      setIsSaveQueryModalVisible(true)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const isReportingTab = String(activeKey) === "5"
      if (!isReportingTab) return

      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault()
        handleSaveClick()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuery])

  const saveTooltipTitle = !selectedQuery
    ? `Save Query (${saveShortcut})`
    : isUnsaved
    ? `Unsaved changes. Save (${saveShortcut})`
    : `Saved (${saveShortcut})`

  return (
    <>
      <ExportModal
        title="Save Query"
        isVisible={isSaveQueryModalVisible}
        confirm={confirmSaveQuery}
        cancel={() => setIsSaveQueryModalVisible(false)}
        message="Please enter a filename for your query"
        ExportButtonText="Save"
      />
      {!hideRun && (
        <Tooltip
          title={
            !canRun
              ? "Add at least one row and one column variable"
              : isRunningScript
              ? "Running…"
              : ""
          }
          placement="top"
          overlayClassName="dataq-tooltip"
        >
          <span className="inline-block">
            <DataQButton
              isDisabled={isRunningScript || !canRun}
              onClick={() => runScript()}
              type="primary"
            >
              Run
              <FontAwesomeIcon icon={faPlay} className="ml-2" />
            </DataQButton>
          </span>
        </Tooltip>
      )}
      {!hideSave && (
        <Tooltip
          title={saveTooltipTitle}
          placement="top"
          overlayClassName="dataq-tooltip"
        >
          <span className="inline-block">
            <DataQButton
              onClick={handleSaveClick}
              type="primary"
              isDisabled={!canRun}
            >
              Save
              <FontAwesomeIcon icon={faSave} className="ml-2" />
            </DataQButton>
          </span>
        </Tooltip>
      )}
      <ExportDataModal queryInstance={queryInstance} />
    </>
  )
}

export default QueryConsoleActions
